import React, {
	memo,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import { arrowLeft, gear } from '@/assets/icons';
import { LayoutContext } from '@/components/Layout';
import { ProfileModal } from '@/components/ProfileModal';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { Loader } from '@/components/shared/Loader/Loader';
import { SheetLayout } from '@/components/shared/SheetLayout';
import {
	DRAFT_SETLISTS_STORAGE_KEY,
	INITIAL_MESSAGES_SIZE,
	LinkFrom,
} from '@/config/constants';
import { dashboardUrl, directMessagesUrl } from '@/config/urls';
import {
	ChatType,
	MessageType,
	useGetChatQuery,
	useSendChatMessageMutation,
	useUpsertChatStatMutation,
} from '@/graphql';
import { formatEnvironmentTitle } from '@/helpers/formatEnvironmentTitle';
import { getChatTitleMembersRecipient } from '@/helpers/getChatChannelInfo';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalytics, useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import { SvgIcon } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useEvent, useLocalStorage } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import styles from './ChatPage.module.scss';
import { ChatHeader } from './components/ChatHeader';
import { PollMessagesAndRenderChatContainer } from './components/PollMessagesAndRenderChatContainer';
import { PrivateGroupSettingsPanel } from './components/PrivateGroupSettingsPanel';
import ShareDialog from './components/ShareDialog/ShareDialog';

type ChatPageParams = {
	token: string;
};
interface ChatPageLocationProps {
	linkFrom: LinkFrom;
}

export const ChatPage: React.FC = () => {
	const navigate = useNavigate();
	const { setChatIds, chatIds } = useUnreadChats();
	const { handleError } = useErrorHandler();
	const { draftSetlists } = useSetlists();

	let { token } = useParams<ChatPageParams>();
	if (!token) {
		navigate(directMessagesUrl);
	}
	const chatId = token || '';

	const { mutateAsync: sendChatMessage } = useSendChatMessageMutation();
	const { data, isLoading, error, refetch } = useGetChatQuery(
		{
			chatId,
			input: {
				limit: INITIAL_MESSAGES_SIZE,
				offset: 0,
			},
		},
		{
			refetchOnWindowFocus: false,
		},
	);

	const chat = data?.getChat;

	const { setHideBottomMenu, openSonglistWithCallback } = useLayout();
	const { user } = useAuth();
	const { environmentString, isLoading: isEnvLoading } = useEnvironment();

	const location = useLocation();
	const { trackPage } = useAnalytics();
	const linkFrom = (location?.state as ChatPageLocationProps)?.linkFrom;

	const [privateGroupSettingsOpen, setPrivateGroupSettingsOpen] =
		useState<boolean>(false);
	const [selectedProfileMemberId, setSelectedProfileMemberId] = useState<
		string | null
	>(null);
	const lastSelectedProfileMemberIdRef = useRef<string | null>(null);
	if (selectedProfileMemberId !== null) lastSelectedProfileMemberIdRef.current = selectedProfileMemberId;

	const [title, setTitle] = useState('');
	const [pageViewTitle, setPageViewTitle] = useState('');
	const [songShareIconEl, setSongShareIconEl] = useState<null | HTMLElement>(
		null,
	);
	const [isShareOpen, setIsShareOpen] = useState(false);

	const fireOpenShareEvent = useAnalyticsEvent('Chat_Open_Share_Songs');
	const fireShareSongsEvent = useAnalyticsEvent('Chat_Share_Songs');
	const fireOpenCreateSetlistEvent = useAnalyticsEvent(
		'Chat_Open_Create_Setlist',
	);

	const openSettings = () => setPrivateGroupSettingsOpen(true);
	const handleBackPress = useCallback(() => {
		if (linkFrom === LinkFrom.messages) {
			navigate(directMessagesUrl);
		} else {
			navigate(dashboardUrl);
		}
	}, [linkFrom, navigate]);

	const sendMessage = useCallback(
		(value, type, metadata: string) => {
			if (chat?.id) {
				const id = uuidv4();
				return sendChatMessage({
					chatId: chat.id,
					input: { id, content: value, type, metadata },
				});
			}
		},
		[sendChatMessage, chat?.id],
	);

	const handleSongShare = useCallback(() => {
		fireOpenShareEvent();
		setSongShareIconEl(null);
		openSonglistWithCallback((songIds) => {
			if (songIds.length) {
				console.log(songIds);
				// When we call the sendChatMessage mutation, a pub-sub event will be sent
				// which will notfiy all subscribers, including the current user. Eventhough he is the
				// actor, there is a "includeActor" field that includes the actor as a recipient.
				// This will trigger fetchNewMessages for us due to TeamActionActions.ChatMessageSent
				sendMessage(
					'Shared songs',
					MessageType.Songlist,
					JSON.stringify(songIds),
				);
				fireShareSongsEvent({
					length: songIds.length,
					songIds,
				});
			}
		});
	}, [
		fireOpenShareEvent,
		fireShareSongsEvent,
		sendMessage,
		openSonglistWithCallback,
	]);

	useEffect(() => {
		setHideBottomMenu(true);
		return () => setHideBottomMenu(false);
	}, [setHideBottomMenu]);

	useEffect(() => {
		if (chat && !isEnvLoading) {
			const { title } = getChatTitleMembersRecipient(
				chat,
				user.id,
				user.displayName,
			);

			setTitle(
				`${formatEnvironmentTitle(
					environmentString,
				)}Asaph | Messages | ${title}`,
			);
			setPageViewTitle(
				`${formatEnvironmentTitle(
					environmentString,
				)}Asaph | Messages | ${
					chat.type === ChatType.Group ? 'Group Chat' : 'DM'
				}`,
			);
		}
	}, [chat, user.id, user.displayName, environmentString, isEnvLoading]);

	useEffect(() => {
		if (pageViewTitle) {
			const type = pageViewTitle.includes('Group') ? 'group-chat' : 'dm';
			trackPage({
				title: pageViewTitle,
				url: `${directMessagesUrl}/[${type}]`,
			});
		}
	}, [pageViewTitle, trackPage]);

	// Mark the chat as "read" by removing it form the
	// global unread chats resource
	useEffect(() => {
		if (chat?.id) {
			setChatIds((chatIds) => chatIds.filter((id) => id !== chat.id));
		}
		// Note that we have chatIds.length in the deps array even though
		// we are not using it. This is done in order to remove the chatId
		// form the list of unread chats while we are on the chat page with
		// every new state of unread chats.
		// The cycle is:
		// 1. The WS notifies us about a change in the chat we are in (due to a new message)
		// 2. The UnreadChatsProvider fetches that new list and the current chat is in there
		// 3. We get the new list via chatIds and we filter it out since we are in it
	}, [setChatIds, chatId, chatIds.length, chat]);

	if (isLoading) {
		return <Loader />;
	}

	if (error || !chat) {
		handleError({
			errorCode: 'LoadChatError',
			friendlyMessage: 'Error while loading chat',
			error,
		});
		navigate(directMessagesUrl);
		return <Loader />;
	}

	return (
		<>
			<Helmet title={title} />
			<SheetLayout
				noPadding
				topComponentClassName={styles['top-component']}
				actions={[
					{
						icon: gear,
						onClick: openSettings,
						hidden: chat.type !== ChatType.Group,
					},
				]}
				titleComponent={
					<div className={styles['header']}>
						<div
							className={styles['back-button']}
							onClick={handleBackPress}
						>
							<SvgIcon component={arrowLeft} />
						</div>
						<ChatHeader
							chat={chat}
							onPress={
								chat.type === ChatType.Group
									? openSettings
									: () => {
											const { recipientIds } =
												getChatTitleMembersRecipient(
													chat,
													user.id,
													user.displayName,
												);
											if (recipientIds[0]) {
												setSelectedProfileMemberId(
													recipientIds[0],
												);
											}
										}
							}
						/>
					</div>
				}
			>
				<PollMessagesAndRenderChatContainer
					chat={chat}
					setSongShareIconEl={setSongShareIconEl}
					handleSongShare={handleSongShare}
				/>
			</SheetLayout>

			<AsaphDropdownMenu
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				open={!!songShareIconEl}
				onClose={() => setSongShareIconEl(null)}
				anchorEl={songShareIconEl}
				items={[
					{
						label: 'Share setlist',
						onClick: () => setIsShareOpen(true),
					},
					{ label: 'Share songs', onClick: handleSongShare },
				]}
				className={styles['song-share-menu']}
			/>

			<PrivateGroupSettingsPanel
				open={privateGroupSettingsOpen}
				setOpen={setPrivateGroupSettingsOpen}
				chat={chat}
				loadingChatChannel={isLoading}
				loadChatChannel={() => refetch()}
			/>

			{isShareOpen && (
				<ShareDialog
					chatId={chat.id}
					onClose={() => {
						setIsShareOpen(false);
						setSongShareIconEl(null);
					}}
				/>
			)}

			<ProfileModal
				memberId={selectedProfileMemberId ?? lastSelectedProfileMemberIdRef.current ?? ''}
				open={selectedProfileMemberId !== null}
				onClose={() => setSelectedProfileMemberId(null)}
			/>
		</>
	);
};
